import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';
import { MingoProvider, useMingo } from './context/MingoContext';
import { ChatInboxProvider } from './context/ChatInboxContext';
import { SessionGate } from './components/SessionGate';
import { fetchMyAppUserRow } from './api/profileApi';
import { useFCM } from './hooks/useFCM';
import { useBookingVoiceAnnouncements } from './hooks/useBookingVoiceAnnouncements';
import { canEnterMainApp } from './lib/onboardingStorage';
import { redirectLegacyProdHostIfNeeded } from './lib/appOrigin';
import AppOverlays from './components/AppOverlays';
import TabLayout from './layout/TabLayout';
import ProtectedRoute from './components/ProtectedRoute';
import FeatureRoute from './components/FeatureRoute';
import RouteFallback from './components/RouteFallback';
import AnalyticsRouteTracker from './components/AnalyticsRouteTracker';
import GlobalScrollRestoration from './components/GlobalScrollRestoration';
import { installMobileViewportFix } from './lib/resetMobileViewport';
import SOSButton from './components/SOSButton';
import ChatBot from './components/ChatBot';
import LocalInfo from './pages/LocalInfo';
import SearchPage from './pages/SearchPage';
const Home = lazy(() => import('./pages/Home'));
const SafePathLifeDetail = lazy(() => import('./pages/SafePathLifeDetail'));
const Community = lazy(() => import('./pages/Community'));
const MyCommunityPosts = lazy(() => import('./pages/MyCommunityPosts'));
const PointHistory = lazy(() => import('./pages/PointHistory'));
const PointCharge = lazy(() => import('./pages/PointCharge'));
const CreditCharge = lazy(() => import('./pages/CreditCharge'));
const RiderJoinPage = lazy(() => import('./pages/rider/RiderJoinPage'));
const RiderDashboardPage = lazy(() => import('./pages/rider/RiderDashboardPage'));
const WalletPage = lazy(() => import('./pages/WalletPage'));
const Chats = lazy(() => import('./pages/Chats'));
const FriendHub = lazy(() => import('./pages/friends/FriendHub'));
const FollowListPage = lazy(() => import('./pages/friends/FollowListPage'));
const ChatRoom = lazy(() => import('./pages/ChatRoom'));
const ChatRoomV2 = lazy(() => import('./pages/chat/ChatRoomV2'));
const ChatListV2 = lazy(() => import('./pages/chat/ChatListV2'));
const VisaInfo = lazy(() => import('./pages/VisaInfo'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const WritePost = lazy(() => import('./pages/WritePost'));
const WriteCommunity = lazy(() => import('./pages/WriteCommunity'));
const PostDetail = lazy(() => import('./pages/PostDetail'));
const Splash = lazy(() => import('./pages/Splash'));
const LanguageSelect = lazy(() => import('./pages/LanguageSelect'));
const OnboardingPreferences = lazy(() => import('./pages/OnboardingPreferences'));
const OAuthConsent = lazy(() => import('./pages/onboarding/OAuthConsent'));
const LocationConfirm = lazy(() => import('./pages/LocationConfirm'));
const LocationSelect = lazy(() => import('./pages/LocationSelect'));
const Auth = lazy(() => import('./pages/Auth'));
const AuthCallback = lazy(() => import('./pages/auth/AuthCallback'));
const WriteReviewPage = lazy(() => import('./pages/review/WriteReviewPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const PaymentReady = lazy(() => import('./pages/PaymentReady'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const RequireAdmin = lazy(() => import('./pages/admin/RequireAdmin'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminBusinesses = lazy(() => import('./pages/admin/AdminBusinesses'));
const AdminBusinessApproval = lazy(() => import('./pages/admin/AdminBusinessApproval'));
const AdminCatalogApproval = lazy(() => import('./pages/admin/AdminCatalogApproval'));
const AdminFoodSubcategories = lazy(() => import('./pages/admin/AdminFoodSubcategories'));
const AdminReviews = lazy(() => import('./pages/admin/AdminReviews'));
const AdminNoShow = lazy(() => import('./pages/admin/AdminNoShow'));
const AdminBookings = lazy(() => import('./pages/admin/AdminBookings'));
const AdminSettlements = lazy(() => import('./pages/admin/AdminSettlements'));
const AdminCreators = lazy(() => import('./pages/admin/AdminCreators'));
const AdminStats = lazy(() => import('./pages/admin/AdminStats'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminAds = lazy(() => import('./pages/admin/AdminAds'));
const AdminAdBotSettings = lazy(() => import('./pages/admin/AdminAdBotSettings'));
const AdminPosts = lazy(() => import('./pages/admin/AdminPosts'));
const AdminScam = lazy(() => import('./pages/admin/AdminScam'));
const AdminScamContacts = lazy(() => import('./pages/admin/AdminScamContacts'));
const AdminNotice = lazy(() => import('./pages/admin/AdminNotice'));
const AdminBusinessVerify = lazy(() => import('./pages/admin/AdminBusinessVerify'));
const AdminBusinessClaims = lazy(() => import('./pages/admin/AdminBusinessClaims'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminSystem = lazy(() => import('./pages/admin/AdminSystem'));
const AdminSystemTab = lazy(() => import('./pages/admin/tabs/AdminSystemTab'));
const AdminTierAHub = lazy(() => import('./pages/admin/AdminTierAHub'));
const Jobs = lazy(() => import('./pages/Jobs'));
const JobsWrite = lazy(() => import('./pages/JobsWrite'));
const JobsOnboarding = lazy(() => import('./pages/JobsOnboarding'));
const MyJobApplications = lazy(() => import('./pages/MyJobApplications'));
const TalentDirectory = lazy(() => import('./pages/TalentDirectory'));
const ScamContactSearch = lazy(() => import('./pages/ScamContactSearch'));
const MyResume = lazy(() => import('./pages/MyResume'));
const CreatorHome = lazy(() => import('./pages/creator/CreatorHome'));
const CreatorQrLanding = lazy(() => import('./pages/creator/CreatorQrLanding'));
const JobSafetyTips = lazy(() => import('./pages/JobSafetyTips'));
const LaborContractTemplate = lazy(() => import('./pages/LaborContractTemplate'));
const MyFollowedEmployers = lazy(() => import('./pages/MyFollowedEmployers'));
const MySavedTalents = lazy(() => import('./pages/MySavedTalents'));
const RealEstate = lazy(() => import('./pages/RealEstate'));
const PropertyDetail = lazy(() => import('./pages/PropertyDetail'));
const RealEstateWrite = lazy(() => import('./pages/RealEstateWrite'));
const MyProperties = lazy(() => import('./pages/MyProperties'));
const BusinessProfile = lazy(() => import('./pages/BusinessProfile'));
const BusinessRegister = lazy(() => import('./pages/BusinessRegister'));
const BusinessClaimSearch = lazy(() => import('./pages/BusinessClaimSearch'));
const BusinessDetail = lazy(() => import('./pages/BusinessDetail'));
const BusinessDetailPage = lazy(() => import('./pages/business/BusinessDetailPage'));
const Cart = lazy(() => import('./pages/Cart'));
const Orders = lazy(() => import('./pages/Orders'));
const BusinessDashboard = lazy(() => import('./pages/BusinessDashboard'));
const BusinessNotifications = lazy(() => import('./pages/BusinessNotifications'));
const CatalogFormRouter = lazy(() => import('./pages/catalog/CatalogFormRouter'));
const CatalogFormRedirect = lazy(() => import('./pages/catalog/CatalogFormRedirect'));
const CatalogEditQueryRedirect = lazy(() =>
  import('./pages/catalog/CatalogFormRedirect').then((m) => ({ default: m.CatalogEditQueryRedirect })),
);
const CatalogDetailPage = lazy(() => import('./pages/catalog/CatalogDetailPage'));
const BrandMenuCatalogPage = lazy(() => import('./pages/business/BrandMenuCatalogPage'));
const BrandPage = lazy(() => import('./pages/hubs/BrandPage'));
const LocationAreaSettingsPage = lazy(() => import('./pages/hubs/LocationAreaSettingsPage'));
const CartCheckoutPage = lazy(() => import('./pages/catalog/CartCheckoutPage'));
const MyCartsPage = lazy(() => import('./pages/catalog/MyCartsPage'));
const QuickRegister = lazy(() => import('./pages/QuickRegister'));
const BusinessRegisterHub = lazy(() => import('./pages/BusinessRegisterHub'));
const BusinessInbox = lazy(() => import('./pages/BusinessInbox'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const LocationRadiusSettings = lazy(() => import('./pages/LocationRadiusSettings'));
const ExpiryReminder = lazy(() => import('./pages/ExpiryReminder'));
const BlockedUsersPage = lazy(() => import('./pages/BlockedUsersPage'));
const FriendSettings = lazy(() => import('./pages/friends/FriendSettings'));
const MyTrades = lazy(() => import('./pages/MyTrades'));
const MyFavorites = lazy(() => import('./pages/MyFavorites'));
const MyReservations = lazy(() => import('./pages/MyReservations'));
const BookingPage = lazy(() => import('./pages/booking/BookingPage'));
const MyBookingsPage = lazy(() => import('./pages/booking/MyBookingsPage'));
const TeamFilePage = lazy(() => import('./pages/booking/TeamFilePage'));
const EditProfile = lazy(() => import('./pages/EditProfile'));
const UserAddressBookPage = lazy(() => import('./pages/UserAddressBookPage'));
const UserAddressSearchPage = lazy(() => import('./pages/UserAddressSearchPage'));
const TravelProfilePage = lazy(() => import('./pages/TravelProfilePage'));
const ContractView = lazy(() => import('./pages/ContractView'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const AdInquiry = lazy(() => import('./pages/AdInquiry'));
const BusinessRegisterInquiry = lazy(() => import('./pages/BusinessRegisterInquiry'));
const AdminAdInquiries = lazy(() => import('./pages/admin/AdminAdInquiries'));
const AdminTravelQuotes = lazy(() => import('./pages/admin/AdminTravelQuotes'));
const AdminProperties = lazy(() => import('./pages/admin/AdminProperties'));
const AdminHotels = lazy(() => import('./pages/admin/AdminHotels'));
const AdminRentals = lazy(() => import('./pages/admin/AdminRentals'));
const AdminHospitals = lazy(() => import('./pages/admin/AdminHospitals'));
const AdminMedicines = lazy(() => import('./pages/admin/AdminMedicines'));
const AdminAcademies = lazy(() => import('./pages/admin/AdminAcademies'));
const AdminRestaurants = lazy(() => import('./pages/admin/AdminRestaurants'));
const AdminVisaServices = lazy(() => import('./pages/admin/AdminVisaServices'));
const AdminShortsQueue = lazy(() => import('./pages/admin/AdminShortsQueue'));
const AdminChargeRequests = lazy(() => import('./pages/admin/AdminChargeRequests'));
const AdminBusinessChargeRequests = lazy(() => import('./pages/admin/AdminBusinessChargeRequests'));
const AdminSubscriptions = lazy(() => import('./pages/admin/AdminSubscriptions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const AboutMingo = lazy(() => import('./pages/AboutMingo'));
const Notifications = lazy(() => import('./pages/Notifications'));
const EmergencyContacts = lazy(() => import('./pages/EmergencyContacts'));
const GroupBuy = lazy(() => import('./pages/GroupBuy'));
const GroupBuyWrite = lazy(() => import('./pages/GroupBuyWrite'));
const Events = lazy(() => import('./pages/Events'));
const LanguageExchange = lazy(() => import('./pages/LanguageExchange'));
const AdPayment = lazy(() => import('./pages/AdPayment'));
const PremiumPayment = lazy(() => import('./pages/PremiumPayment'));
const Payment = lazy(() => import('./pages/Payment'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const FortuneMenu = lazy(() => import('./pages/fortune/FortuneMenu'));
const OdaysFortune = lazy(() => import('./pages/fortune/OdaysFortune'));
const Horoscope = lazy(() => import('./pages/fortune/Horoscope'));
const ZodiacFortune = lazy(() => import('./pages/fortune/ZodiacFortune'));
const YearFortune = lazy(() => import('./pages/fortune/YearFortune'));
const Astrology = lazy(() => import('./pages/fortune/Astrology'));
const Tarot = lazy(() => import('./pages/fortune/Tarot'));
const Saju = lazy(() => import('./pages/fortune/Saju'));
const Tojeong = lazy(() => import('./pages/fortune/Tojeong'));
const Compatibility = lazy(() => import('./pages/fortune/Compatibility'));
const DreamFortune = lazy(() => import('./pages/fortune/DreamFortune'));
const NameFortune = lazy(() => import('./pages/fortune/NameFortune'));
const LocalHospital = lazy(() => import('./pages/local/LocalHospital'));
const LocalRestaurantExplore = lazy(() => import('./pages/local/LocalRestaurantExplore'));
const LocalVisa = lazy(() => import('./pages/local/LocalVisa'));
const LocalBeauty = lazy(() => import('./pages/local/LocalBeauty'));
const LocalCleaning = lazy(() => import('./pages/local/LocalCleaning'));
const LocalReturn = lazy(() => import('./pages/local/LocalReturn'));
const LocalSupermarket = lazy(() => import('./pages/local/LocalSupermarket'));
const LocalDental = lazy(() => import('./pages/local/LocalDental'));
const LocalLaundry = lazy(() => import('./pages/local/LocalLaundry'));
const LocalRepair = lazy(() => import('./pages/local/LocalRepair'));
const LocalBabysitter = lazy(() => import('./pages/local/LocalBabysitter'));
const LocalTranslation = lazy(() => import('./pages/local/LocalTranslation'));
const CurrencyExchange = lazy(() => import('./pages/CurrencyExchange'));
const ExchangeWrite = lazy(() => import('./pages/ExchangeWrite'));
const ExchangeDetail = lazy(() => import('./pages/ExchangeDetail'));
const ExchangeAlert = lazy(() => import('./pages/ExchangeAlert'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const Experiences = lazy(() => import('./pages/Experiences'));
const ExperienceDetail = lazy(() => import('./pages/ExperienceDetail'));
const ExperienceRegister = lazy(() => import('./pages/ExperienceRegister'));
const GroupBuyDetail = lazy(() => import('./pages/GroupBuyDetail'));
const VisaRun = lazy(() => import('./pages/VisaRun'));
const VisaRunDetail = lazy(() => import('./pages/VisaRunDetail'));
const VisaRunWrite = lazy(() => import('./pages/VisaRunWrite'));
const BusinessTransfer = lazy(() => import('./pages/BusinessTransfer'));
const BusinessTransferDetail = lazy(() => import('./pages/BusinessTransferDetail'));
const BusinessTransferWrite = lazy(() => import('./pages/BusinessTransferWrite'));
const EventDetail = lazy(() => import('./pages/EventDetail'));
const BusinessReviews = lazy(() => import('./pages/BusinessReviews'));
const RegionLanding = lazy(() => import('./pages/RegionLanding'));
const CourseDetail = lazy(() => import('./pages/CourseDetail'));
const RentalDetail = lazy(() => import('./pages/RentalDetail'));
const VisaService = lazy(() => import('./pages/VisaService'));
const VisaServiceDetail = lazy(() => import('./pages/VisaServiceDetail'));
const VisaServiceRegister = lazy(() => import('./pages/VisaServiceRegister'));
const ProfessionalServices = lazy(() => import('./pages/ProfessionalServices'));
const ProfessionalDetail = lazy(() => import('./pages/ProfessionalDetail'));
const Academy = lazy(() => import('./pages/Academy'));
const AcademyRegister = lazy(() => import('./pages/AcademyRegister'));
const AcademyDetail = lazy(() => import('./pages/AcademyDetail'));
const Restaurant = lazy(() => import('./pages/Restaurant'));
const RestaurantRegister = lazy(() => import('./pages/RestaurantRegister'));
const RestaurantDetail = lazy(() => import('./pages/RestaurantDetail'));
const Hospital = lazy(() => import('./pages/Hospital'));
const HospitalDetail = lazy(() => import('./pages/HospitalDetail'));
const HospitalRegister = lazy(() => import('./pages/HospitalRegister'));
const ActivityList = lazy(() => import('./pages/ActivityList'));
const ActivityDetail = lazy(() => import('./pages/ActivityDetail'));
const PetServices = lazy(() => import('./pages/PetServices'));
const PetServiceDetail = lazy(() => import('./pages/PetServiceDetail'));
const PetRegister = lazy(() => import('./pages/PetRegister'));
const VehicleList = lazy(() => import('./pages/VehicleList'));
const VehicleDetail = lazy(() => import('./pages/VehicleDetail'));
const MentorList = lazy(() => import('./pages/MentorList'));
const MentorDetail = lazy(() => import('./pages/MentorDetail'));
const MentorRegister = lazy(() => import('./pages/MentorRegister'));
const KoreanProducts = lazy(() => import('./pages/KoreanProducts'));
const KoreanProductDetail = lazy(() => import('./pages/KoreanProductDetail'));
const NewsPage = lazy(() => import('./pages/NewsPage'));
const NewsDetail = lazy(() => import('./pages/NewsDetail'));
const WebtoonPage = lazy(() => import('./pages/WebtoonPage'));
const ShortsPage = lazy(() => import('./pages/ShortsPage'));
const StoryCreatePage = lazy(() => import('./pages/StoryCreatePage'));
const StoryRevenuePanel = lazy(() => import('./pages/StoryRevenuePanel'));
const StoryTranslationDashboard = lazy(() =>
  import('./pages/StoryTranslationDashboard'),
);
const WeddingServices = lazy(() => import('./pages/WeddingServices'));
const WeddingDetail = lazy(() => import('./pages/WeddingDetail'));
const WeddingRegister = lazy(() => import('./pages/WeddingRegister'));
const AdvertiserDashboard = lazy(() => import('./pages/AdvertiserDashboard'));
const SurveyList = lazy(() => import('./pages/SurveyList'));
const AirportPickup = lazy(() => import('./pages/AirportPickup'));
const CarpoolBoard = lazy(() => import('./pages/CarpoolBoard'));
const InsuranceList = lazy(() => import('./pages/InsuranceList'));
const InsuranceRegister = lazy(() => import('./pages/InsuranceRegister'));
const RemittanceList = lazy(() => import('./pages/RemittanceList'));
const RemittanceRegister = lazy(() => import('./pages/RemittanceRegister'));
const LocalDeals = lazy(() => import('./pages/LocalDeals'));
const SettlementPackage = lazy(() => import('./pages/SettlementPackage'));
const SchoolList = lazy(() => import('./pages/SchoolList'));
const SchoolCompare = lazy(() => import('./pages/SchoolCompare'));
const SchoolDetail = lazy(() => import('./pages/SchoolDetail'));
const Education = lazy(() => import('./pages/Education'));
const EducationConsultantList = lazy(() => import('./pages/EducationConsultantList'));
const FitnessList = lazy(() => import('./pages/FitnessList'));
const GolfList = lazy(() => import('./pages/GolfList'));
const GolfDetail = lazy(() => import('./pages/GolfDetail'));
const GolfHub = lazy(() => import('./pages/Golf'));
const GolfMembershipDetail = lazy(() => import('./pages/GolfMembershipDetail'));
const GolfRoundDetail = lazy(() => import('./pages/GolfRoundDetail'));
const GolfRoundWrite = lazy(() => import('./pages/GolfRoundWrite'));
const GolfClubDetail = lazy(() => import('./pages/GolfClubDetail'));
const GolfClubWrite = lazy(() => import('./pages/GolfClubWrite'));
const GolfMarketWrite = lazy(() => import('./pages/GolfMarketWrite'));
const GolfMarketDetail = lazy(() => import('./pages/GolfMarketDetail'));
const MedicalTourism = lazy(() => import('./pages/MedicalTourism'));
const BoatRental = lazy(() => import('./pages/BoatRental'));
const PhotoService = lazy(() => import('./pages/PhotoService'));
const PhotoRegister = lazy(() => import('./pages/PhotoRegister'));
const EventPlanner = lazy(() => import('./pages/EventPlanner'));
const DigitalServices = lazy(() => import('./pages/DigitalServices'));
const DigitalRegister = lazy(() => import('./pages/DigitalRegister'));
const TourDetail = lazy(() => import('./pages/TourDetail'));
const TourList = lazy(() => import('./pages/TourList'));
const InteriorList = lazy(() => import('./pages/InteriorList'));
const InteriorRegister = lazy(() => import('./pages/InteriorRegister'));
const TalentMarket = lazy(() => import('./pages/TalentMarket'));
const TalentDetail = lazy(() => import('./pages/TalentDetail'));
const TalentRegister = lazy(() => import('./pages/TalentRegister'));
const SubscriptionBox = lazy(() => import('./pages/SubscriptionBox'));
const GroupChatList = lazy(() => import('./pages/GroupChatList'));
const GroupDetail = lazy(() => import('./pages/groups/GroupDetail'));
const GroupManage = lazy(() => import('./pages/groups/GroupManage'));
const GroupCreate = lazy(() => import('./pages/groups/GroupCreate'));
const MyGroups = lazy(() => import('./pages/MyGroups'));
const LuxuryMarket = lazy(() => import('./pages/LuxuryMarket'));
const BookingHub = lazy(() => import('./pages/BookingHub'));
const AllServicesPage = lazy(() => import('./pages/AllServicesPage'));
const GenericHubPage = lazy(() => import('./pages/hubs/GenericHubPage'));
const FoodMartHubPage = lazy(() => import('./pages/hubs/FoodMartHubPage'));
const FoodMartEventsHubPage = lazy(() => import('./pages/hubs/FoodMartEventsHubPage'));
const CuisineCategoryPage = lazy(() => import('./pages/hubs/CuisineCategoryPage'));
const FlightSearch = lazy(() => import('./pages/FlightSearch'));
const Hotel = lazy(() => import('./pages/Hotel'));
const PoolVilla = lazy(() => import('./pages/PoolVilla'));
const PoolVillaDetail = lazy(() => import('./pages/PoolVillaDetail'));
const PoolVillaRegister = lazy(() => import('./pages/PoolVillaRegister'));
const HotelDetail = lazy(() => import('./pages/HotelDetail'));
const HotelRegister = lazy(() => import('./pages/HotelRegister'));
const Rental = lazy(() => import('./pages/Rental'));
const RentalRegister = lazy(() => import('./pages/RentalRegister'));
const RentalMyBookings = lazy(() => import('./pages/RentalMyBookings'));
const LiveStream = lazy(() => import('./pages/LiveStream'));
const Crowdfunding = lazy(() => import('./pages/Crowdfunding'));
const CoworkingList = lazy(() => import('./pages/CoworkingList'));
const CoworkingRegister = lazy(() => import('./pages/CoworkingRegister'));
const RecipeList = lazy(() => import('./pages/RecipeList'));
const PropertyAuction = lazy(() => import('./pages/PropertyAuction'));
const Rewards = lazy(() => import('./pages/Rewards'));
const MapSearch = lazy(() => import('./pages/MapSearch'));
const NewsletterList = lazy(() => import('./pages/NewsletterList'));
const ProductAuction = lazy(() => import('./pages/ProductAuction'));
const RiderSignup = lazy(() => import('./pages/RiderSignup'));
const FinancialPlanner = lazy(() => import('./pages/FinancialPlanner'));
const PointMarket = lazy(() => import('./pages/PointMarket'));
const PenaltyRecovery = lazy(() => import('./pages/PenaltyRecovery'));
const Medicine = lazy(() => import('./pages/Medicine'));
const MedicineDetail = lazy(() => import('./pages/MedicineDetail'));
const TravelHub = lazy(() => import('./pages/TravelHub'));
const GuideMarket = lazy(() => import('./pages/GuideMarket'));
const GuideDetail = lazy(() => import('./pages/GuideDetail'));
const GuideRegister = lazy(() => import('./pages/GuideRegister'));
const SosPage = lazy(() => import('./pages/SosPage'));
const LostAndFound = lazy(() => import('./pages/LostAndFound'));
const ExpatInfo = lazy(() => import('./pages/ExpatInfo'));
const ExpatInfoCategory = lazy(() => import('./pages/ExpatInfoCategory'));
const ExpatInfoDetail = lazy(() => import('./pages/ExpatInfoDetail'));
const ExpatLegalAdminPage = lazy(() => import('./pages/ExpatLegalAdminPage'));
const ExpatSchoolInfoPage = lazy(() => import('./pages/ExpatSchoolInfoPage'));
const GroupDelivery = lazy(() => import('./pages/GroupDelivery'));
const InterpreterService = lazy(() => import('./pages/InterpreterService'));
const ListingHub = lazy(() => import('./pages/listings/ListingHub'));
const CategoryListingsPage = lazy(() => import('./pages/listings/CategoryListingsPage'));
const ListingDetailPage = lazy(() => import('./pages/listings/ListingDetailPage'));
const BookingTransferList = lazy(() => import('./pages/transfer/BookingTransferList'));
const BookingTransferWrite = lazy(() => import('./pages/transfer/BookingTransferWrite'));
const BookingTransferDetail = lazy(() => import('./pages/transfer/BookingTransferDetail'));
const SuccessionList = lazy(() => import('./pages/succession/SuccessionList'));
const SuccessionWrite = lazy(() => import('./pages/succession/SuccessionWrite'));
const SuccessionDetail = lazy(() => import('./pages/succession/SuccessionDetail'));

function RootRedirect() {
  return <Navigate to={canEnterMainApp() ? '/home' : '/splash'} replace />;
}

function CatchAllRedirect() {
  return <Navigate to={canEnterMainApp() ? '/home' : '/splash'} replace />;
}

function GuideMarketDetailLegacyRedirect() {
  const { id } = useParams();
  return <Navigate to={`/guides/${id}`} replace />;
}

function AppFcmBridge() {
  const { isLoggedIn, isGuest, language } = useMingo();
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    if (!isLoggedIn || isGuest) {
      setCurrentUser(null);
      return undefined;
    }
    let cancelled = false;
    void fetchMyAppUserRow().then((me) => {
      if (!cancelled) setCurrentUser(me);
    });
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, isGuest]);

  useFCM(currentUser?.id);
  // 고객이 앱을 화면에 켜둔 상태에서 예약 확정/완료 알림이 오면 음성으로 읽어줌
  // (배달앱의 "배달이 완료되었습니다" 음성 안내와 같은 패턴, 예약 도메인에 한정 적용)
  useBookingVoiceAnnouncements(currentUser?.id, language);
  return null;
}

const AUTH_CALLBACK_PATH = '/auth/callback';
const PASSWORD_RECOVERY_STORAGE_KEY = 'mingo_password_recovery';

/** Supabase password-reset emails use hash type=recovery; OAuth uses access_token without that type. */
function isPasswordRecoveryHash(hash) {
  if (!hash) return false;
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  return params.get('type') === 'recovery';
}

function isOAuthReturnUrl(search, hash) {
  if (search && new URLSearchParams(search).has('code')) return true;
  if (!hash) return false;
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  return params.has('access_token');
}

function RecoveryHashRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const pathname = window.location.pathname;
    const hash = window.location.hash || '';
    const search = window.location.search || '';

    // 비밀번호 재설정 링크가 /auth/callback 으로 떨어진 경우 → 리셋 페이지로 보냄 (플래그 유지)
    if (pathname === AUTH_CALLBACK_PATH && isPasswordRecoveryHash(hash)) {
      sessionStorage.setItem(PASSWORD_RECOVERY_STORAGE_KEY, 'true');
      navigate(`/reset-password${hash}`, { replace: true });
      return;
    }

    if (pathname === AUTH_CALLBACK_PATH) {
      sessionStorage.removeItem(PASSWORD_RECOVERY_STORAGE_KEY);
      return;
    }

    // OAuth가 Site URL(루트·reset-password 등)로 떨어지면 콜백으로 보냄 — access_token을 recovery로 오인하지 않음
    if (isOAuthReturnUrl(search, hash) && !isPasswordRecoveryHash(hash)) {
      sessionStorage.removeItem(PASSWORD_RECOVERY_STORAGE_KEY);
      navigate(`${AUTH_CALLBACK_PATH}${search}${hash}`, { replace: true });
      return;
    }

    if (isPasswordRecoveryHash(hash)) {
      sessionStorage.setItem(PASSWORD_RECOVERY_STORAGE_KEY, 'true');
    }

    const escapePaths = ['/onboarding/auth', '/forgot-password', '/onboarding/oauth-consent'];
    if (escapePaths.includes(pathname)) {
      sessionStorage.removeItem(PASSWORD_RECOVERY_STORAGE_KEY);
      return;
    }

    const hasRecoveryFlag = sessionStorage.getItem(PASSWORD_RECOVERY_STORAGE_KEY) === 'true';
    if (!hasRecoveryFlag) return;

    if (pathname !== '/reset-password') {
      navigate(`/reset-password${hash}`, { replace: true });
    }
  }, [navigate]);

  return null;
}

function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={`${location.pathname}${location.search}`}>
      {children}
    </ErrorBoundary>
  );
}

function AppShell() {
  useEffect(() => {
    redirectLegacyProdHostIfNeeded();
    installMobileViewportFix();
  }, []);

  return (
    <>
      <ChatInboxProvider>
      <AppFcmBridge />
      <div className="mingo-app-viewport">
      <BrowserRouter>
        <RouteErrorBoundary>
        <RecoveryHashRedirect />
        <AnalyticsRouteTracker />
        <GlobalScrollRestoration />
        <AppOverlays />
        <SOSButton />
        <ChatBot />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/splash" element={<Splash />} />
            <Route path="/onboarding/auth" element={<Auth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/review/write/:businessId" element={<WriteReviewPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/onboarding/language" element={<LanguageSelect />} />
            <Route path="/onboarding/location-confirm" element={<LocationConfirm />} />
            <Route path="/onboarding/location-select" element={<LocationSelect />} />
            <Route path="/onboarding/preferences" element={<OnboardingPreferences />} />
            <Route path="/onboarding/oauth-consent" element={<OAuthConsent />} />
            <Route path="/onboarding" element={<LanguageSelect />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/refund" element={<RefundPolicy />} />
            <Route path="/about" element={<AboutMingo />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/profile/:userId" element={<UserProfile />} />
            <Route
              path="/jobs/:jobId"
              element={
                <FeatureRoute flag="jobs">
                  <JobDetail />
                </FeatureRoute>
              }
            />
            <Route
              path="/experiences/:id"
              element={
                <FeatureRoute flag="experiences">
                  <ExperienceDetail />
                </FeatureRoute>
              }
            />
            <Route
              path="/experiences"
              element={
                <FeatureRoute flag="experiences">
                  <Experiences />
                </FeatureRoute>
              }
            />
            <Route
              path="/experiences/register"
              element={
                <FeatureRoute flag="experiences">
                  <ProtectedRoute>
                    <ExperienceRegister />
                  </ProtectedRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/group-buy/:id"
              element={
                <FeatureRoute flag="group_buy">
                  <GroupBuyDetail />
                </FeatureRoute>
              }
            />
            <Route
              path="/visa-run/:id"
              element={
                <FeatureRoute flag="visa_run">
                  <VisaRunDetail />
                </FeatureRoute>
              }
            />
            <Route
              path="/business-transfer/:id"
              element={
                <FeatureRoute flag="business_transfer">
                  <BusinessTransferDetail />
                </FeatureRoute>
              }
            />
            <Route
              path="/events/:id"
              element={
                <FeatureRoute flag="events">
                  <EventDetail />
                </FeatureRoute>
              }
            />
            <Route path="/businesses/:id/reviews" element={<BusinessReviews />} />
            <Route path="/regions/:code" element={<RegionLanding />} />
            <Route path="/courses/:id" element={<CourseDetail />} />
            <Route path="/professional/:id" element={<ProfessionalDetail />} />
            <Route
              path="/academy"
              element={
                <FeatureRoute flag="academy">
                  <Academy />
                </FeatureRoute>
              }
            />
            <Route
              path="/academy/register"
              element={
                <FeatureRoute flag="academy">
                  <ProtectedRoute>
                    <AcademyRegister />
                  </ProtectedRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/academy/:id"
              element={
                <FeatureRoute flag="academy">
                  <AcademyDetail />
                </FeatureRoute>
              }
            />
            <Route
              path="/restaurant"
              element={
                <FeatureRoute flag="restaurant">
                  <Restaurant />
                </FeatureRoute>
              }
            />
            <Route
              path="/restaurant/register"
              element={
                <FeatureRoute flag="restaurant">
                  <ProtectedRoute>
                    <RestaurantRegister />
                  </ProtectedRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/restaurant/:id"
              element={
                <FeatureRoute flag="restaurant">
                  <RestaurantDetail />
                </FeatureRoute>
              }
            />
            <Route path="/activity/:id" element={<ActivityDetail />} />
            <Route path="/pet/:id" element={<PetServiceDetail />} />
            <Route path="/vehicles/:id" element={<VehicleDetail />} />
            <Route path="/mentor/:id" element={<MentorDetail />} />
            <Route path="/korean-shop/:id" element={<KoreanProductDetail />} />
            <Route path="/news/:id" element={<NewsDetail />} />
            <Route path="/wedding/:id" element={<WeddingDetail />} />
            <Route path="/schools/:id" element={<SchoolDetail />} />
            <Route
              path="/golf/membership/:id"
              element={
                <FeatureRoute flag="golf">
                  <GolfMembershipDetail />
                </FeatureRoute>
              }
            />
            <Route
              path="/golf/market/write"
              element={
                <FeatureRoute flag="golf">
                  <ProtectedRoute>
                    <GolfMarketWrite />
                  </ProtectedRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/golf/market/:id"
              element={
                <FeatureRoute flag="golf">
                  <GolfMarketDetail />
                </FeatureRoute>
              }
            />
            <Route
              path="/golf/round/write"
              element={
                <FeatureRoute flag="golf">
                  <ProtectedRoute>
                    <GolfRoundWrite />
                  </ProtectedRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/golf/round/:id"
              element={
                <FeatureRoute flag="golf">
                  <GolfRoundDetail />
                </FeatureRoute>
              }
            />
            <Route
              path="/golf/club/write"
              element={
                <FeatureRoute flag="golf">
                  <ProtectedRoute>
                    <GolfClubWrite />
                  </ProtectedRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/golf/club/:id"
              element={
                <FeatureRoute flag="golf">
                  <GolfClubDetail />
                </FeatureRoute>
              }
            />
            <Route
              path="/golf"
              element={
                <FeatureRoute flag="golf">
                  <GolfHub />
                </FeatureRoute>
              }
            />
            <Route path="/golf/:id" element={<GolfDetail />} />
            <Route path="/tours/:id" element={<TourDetail />} />
            <Route path="/product/:productId" element={<ProductDetail />} />
            <Route
              path="/chat/v2/:roomId"
              element={
                <ProtectedRoute>
                  <ChatRoomV2 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat/v2"
              element={
                <ProtectedRoute>
                  <ChatListV2 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat/:chatId"
              element={
                <ProtectedRoute>
                  <ChatRoom />
                </ProtectedRoute>
              }
            />
            <Route path="/visa-info" element={<VisaInfo />} />
            <Route path="/mypage/visa" element={<VisaInfo />} />
            <Route path="/emergency-contacts" element={<EmergencyContacts />} />
            <Route path="/emergency" element={<EmergencyContacts />} />
            <Route path="/chat" element={<Navigate to="/chats" replace />} />
            <Route
              path="/write"
              element={
                <ProtectedRoute>
                  <WritePost />
                </ProtectedRoute>
              }
            />
            <Route
              path="/community/write"
              element={
                <ProtectedRoute>
                  <WriteCommunity />
                </ProtectedRoute>
              }
            />
            <Route path="/post/:postId" element={<PostDetail />} />
            <Route path="/payment/ready" element={<PaymentReady />} />
            <Route path="/penalty-recovery" element={<PenaltyRecovery />} />
            <Route
              path="/jobs"
              element={
                <FeatureRoute flag="jobs">
                  <Jobs />
                </FeatureRoute>
              }
            />
            <Route
              path="/property/:id"
              element={
                <FeatureRoute flag="real_estate">
                  <PropertyDetail />
                </FeatureRoute>
              }
            />
            <Route
              path="/real-estate/write"
              element={
                <FeatureRoute flag="real_estate">
                  <ProtectedRoute>
                    <RealEstateWrite />
                  </ProtectedRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/jobs/write"
              element={
                <FeatureRoute flag="jobs">
                  <ProtectedRoute>
                    <JobsWrite />
                  </ProtectedRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/jobs/onboarding"
              element={
                <FeatureRoute flag="jobs">
                  <ProtectedRoute>
                    <JobsOnboarding />
                  </ProtectedRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/jobs/my-applications"
              element={
                <FeatureRoute flag="jobs">
                  <ProtectedRoute>
                    <MyJobApplications />
                  </ProtectedRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/jobs/talent"
              element={
                <FeatureRoute flag="jobs">
                  <TalentDirectory />
                </FeatureRoute>
              }
            />
            <Route
              path="/jobs/scam-search"
              element={
                <FeatureRoute flag="jobs">
                  <ScamContactSearch />
                </FeatureRoute>
              }
            />
            <Route
              path="/jobs/resume"
              element={
                <FeatureRoute flag="jobs">
                  <ProtectedRoute>
                    <MyResume />
                  </ProtectedRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/creator"
              element={
                <ProtectedRoute>
                  <CreatorHome />
                </ProtectedRoute>
              }
            />
            <Route path="/c/:qrToken" element={<CreatorQrLanding />} />
            <Route
              path="/jobs/safety-tips"
              element={
                <FeatureRoute flag="jobs">
                  <JobSafetyTips />
                </FeatureRoute>
              }
            />
            <Route
              path="/jobs/contract-template"
              element={
                <FeatureRoute flag="jobs">
                  <LaborContractTemplate />
                </FeatureRoute>
              }
            />
            <Route
              path="/jobs/favorite-employers"
              element={
                <FeatureRoute flag="jobs">
                  <ProtectedRoute>
                    <MyFollowedEmployers />
                  </ProtectedRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/jobs/saved-talents"
              element={
                <FeatureRoute flag="jobs">
                  <ProtectedRoute>
                    <MySavedTalents />
                  </ProtectedRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/business"
              element={
                <ProtectedRoute>
                  <BusinessProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-profile"
              element={
                <ProtectedRoute>
                  <BusinessProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business/register"
              element={
                <ProtectedRoute>
                  <BusinessRegister />
                </ProtectedRoute>
              }
            />
            <Route path="/business/claim" element={<BusinessClaimSearch />} />
            <Route path="/business/:businessId" element={<BusinessDetail />} />
            <Route path="/biz/:id" element={<BusinessDetailPage />} />
            <Route path="/cart" element={<Cart />} />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-dashboard"
              element={
                <ProtectedRoute>
                  <BusinessDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-dashboard/:id"
              element={
                <ProtectedRoute>
                  <BusinessDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-notifications"
              element={
                <ProtectedRoute>
                  <BusinessNotifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/catalog/new"
              element={
                <ProtectedRoute>
                  <CatalogFormRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/catalog/edit"
              element={
                <ProtectedRoute>
                  <CatalogEditQueryRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/catalog/:id/edit"
              element={
                <ProtectedRoute>
                  <CatalogFormRouter />
                </ProtectedRoute>
              }
            />
            <Route path="/catalog/:id" element={<CatalogDetailPage />} />
            <Route path="/biz/:businessId/checkout" element={<CartCheckoutPage />} />
            <Route path="/catalog/cart/:businessId" element={<CartCheckoutPage />} />
            <Route path="/catalog/carts" element={<MyCartsPage />} />
            <Route path="/brand/:brandId" element={<BrandPage />} />
            <Route
              path="/business-dashboard/:id/catalog/new"
              element={
                <ProtectedRoute>
                  <CatalogFormRedirect kind="new" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-dashboard/:id/catalog/:itemId/edit"
              element={
                <ProtectedRoute>
                  <CatalogFormRedirect kind="edit" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-dashboard/:id/brand-catalog"
              element={
                <ProtectedRoute>
                  <BrandMenuCatalogPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quick-register"
              element={
                <ProtectedRoute>
                  <QuickRegister />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-register-hub"
              element={
                <ProtectedRoute>
                  <BusinessRegisterHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-inbox"
              element={
                <ProtectedRoute>
                  <BusinessInbox />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-businesses"
              element={
                <ProtectedRoute>
                  <BusinessDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/ad-inquiry" element={<AdInquiry />} />
            <Route path="/business-register-inquiry" element={<BusinessRegisterInquiry />} />
            <Route
              path="/payment"
              element={
                <ProtectedRoute>
                  <Payment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment/success"
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments/ad"
              element={
                <ProtectedRoute>
                  <AdPayment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments/premium"
              element={
                <ProtectedRoute>
                  <PremiumPayment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/notifications"
              element={
                <ProtectedRoute>
                  <NotificationSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/location-radius"
              element={
                <ProtectedRoute>
                  <LocationRadiusSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/expiry-reminder"
              element={
                <ProtectedRoute>
                  <ExpiryReminder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/blocked-users"
              element={
                <ProtectedRoute>
                  <BlockedUsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/friends"
              element={
                <ProtectedRoute>
                  <FriendSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/account"
              element={
                <ProtectedRoute>
                  <EditProfile />
                </ProtectedRoute>
              }
            />
            <Route path="/edit-profile" element={<Navigate to="/settings/account" replace />} />
            <Route
              path="/my/addresses"
              element={
                <ProtectedRoute>
                  <UserAddressBookPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my/addresses/new"
              element={
                <ProtectedRoute>
                  <UserAddressSearchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my/travel-profile"
              element={
                <ProtectedRoute>
                  <TravelProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contract/:id"
              element={
                <ProtectedRoute>
                  <ContractView />
                </ProtectedRoute>
              }
            />
            <Route path="/admin-mingo-2024" element={<Outlet />}>
              <Route index element={<AdminLogin />} />
              <Route element={<RequireAdmin />}>
                <Route element={<AdminLayout />}>
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="businesses" element={<AdminBusinesses />} />
                  <Route path="business-approval" element={<AdminBusinessApproval />} />
                  <Route path="catalog-approval" element={<AdminCatalogApproval />} />
                  <Route path="food-subcategories" element={<AdminFoodSubcategories />} />
                  <Route path="reviews" element={<AdminReviews />} />
                  <Route path="no-shows" element={<AdminNoShow />} />
                  <Route path="bookings" element={<AdminBookings />} />
                  <Route path="settlements" element={<AdminSettlements />} />
                  <Route path="creators" element={<AdminCreators />} />
                  <Route path="stats" element={<AdminStats />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="ads" element={<AdminAds />} />
                  <Route path="ad-bot-settings" element={<AdminAdBotSettings />} />
                  <Route path="posts" element={<AdminPosts />} />
                  <Route path="properties" element={<AdminProperties />} />
                  <Route path="hotels" element={<AdminHotels />} />
                  <Route path="rentals" element={<AdminRentals />} />
                  <Route path="hospitals" element={<AdminHospitals />} />
                  <Route path="medicines" element={<AdminMedicines />} />
                  <Route path="academies" element={<AdminAcademies />} />
                  <Route path="restaurants" element={<AdminRestaurants />} />
                  <Route path="visa-services" element={<AdminVisaServices />} />
                  <Route path="scam" element={<AdminScam />} />
                  <Route path="scam-contacts" element={<AdminScamContacts />} />
                  <Route path="notice" element={<AdminNotice />} />
                  <Route path="business-verify" element={<AdminBusinessVerify />} />
                  <Route path="business-claims" element={<AdminBusinessClaims />} />
                  <Route path="reports" element={<AdminReports />} />
                  <Route path="tier-a" element={<AdminTierAHub />} />
                  <Route path="appeals" element={<Navigate to="/admin-mingo-2024/tier-a?tab=moderation" replace />} />
                  <Route path="campaigns" element={<Navigate to="/admin-mingo-2024/tier-a?tab=campaigns" replace />} />
                  <Route
                    path="system-ops"
                    element={
                      <AdminSystemTab
                        isSuperAdmin={
                          typeof window !== 'undefined' &&
                          window.localStorage.getItem('mingo_admin_role') === 'super_admin'
                        }
                      />
                    }
                  />
                  <Route path="system" element={<AdminSystem />} />
                  <Route path="inquiries" element={<AdminAdInquiries />} />
                  <Route path="travel-quotes" element={<AdminTravelQuotes />} />
                  <Route path="shorts-queue" element={<AdminShortsQueue />} />
                  <Route path="charge-requests" element={<AdminChargeRequests />} />
                  <Route path="biz-charge-requests" element={<AdminBusinessChargeRequests />} />
                  <Route path="subscriptions" element={<AdminSubscriptions />} />
                  <Route path="*" element={<Navigate to="/admin-mingo-2024/dashboard" replace />} />
                </Route>
              </Route>
            </Route>
            <Route path="/" element={<RootRedirect />} />
            <Route element={<TabLayout />}>
              <Route path="/home" element={<Home />} />
              <Route path="/search" element={<SearchPage />} />
              <Route
                path="/trade"
                element={
                  <Navigate to="/shorts" replace />
                }
              />
              <Route path="/life" element={<LocalInfo />} />
              <Route path="/safepath-life/:country/:city/:slug" element={<SafePathLifeDetail />} />
              <Route path="/local-info" element={<LocalInfo />} />
              <Route path="/expat-info" element={<ExpatInfo />} />
              <Route path="/expat-info/legal-admin" element={<ExpatLegalAdminPage />} />
              <Route path="/expat-info/school-info" element={<ExpatSchoolInfoPage />} />
              <Route path="/expat-info/category/:categoryKey" element={<ExpatInfoCategory />} />
              <Route path="/expat-info/:id" element={<ExpatInfoDetail />} />
              <Route
                path="/my/community-posts"
                element={
                  <FeatureRoute flag="community">
                    <MyCommunityPosts />
                  </FeatureRoute>
                }
              />
              <Route
                path="/community"
                element={
                  <FeatureRoute flag="community">
                    <Community />
                  </FeatureRoute>
                }
              />
              <Route
                path="/chats"
                element={
                  <FeatureRoute flag="chat">
                    <ProtectedRoute>
                      <Chats />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/friends"
                element={
                  <ProtectedRoute>
                    <FriendHub />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/friends/:type"
                element={
                  <ProtectedRoute>
                    <FollowListPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/friends/find" element={<Navigate to="/friends?tab=find" replace />} />
              <Route path="/profile" element={<Navigate to="/settings/account" replace />} />
              <Route
                path="/my/points"
                element={
                  <ProtectedRoute>
                    <PointHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/points/charge"
                element={
                  <ProtectedRoute>
                    <PointCharge />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/credits/charge"
                element={
                  <ProtectedRoute>
                    <CreditCharge />
                  </ProtectedRoute>
                }
              />
              <Route path="/rider/join" element={<RiderJoinPage />} />
              <Route path="/rider" element={<RiderDashboardPage />} />
              <Route
                path="/wallet"
                element={
                  <ProtectedRoute>
                    <WalletPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-trades"
                element={
                  <ProtectedRoute>
                    <MyTrades />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-favorites"
                element={
                  <ProtectedRoute>
                    <MyFavorites />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-reservations"
                element={
                  <ProtectedRoute>
                    <MyReservations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-bookings"
                element={
                  <ProtectedRoute>
                    <MyBookingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/booking/:packageId"
                element={
                  <ProtectedRoute>
                    <BookingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/booking/teamfile/:bookingId"
                element={
                  <ProtectedRoute>
                    <TeamFilePage />
                  </ProtectedRoute>
                }
              />
              <Route path="/app-settings" element={<Navigate to="/settings/account" replace />} />
              <Route path="/legal/:kind" element={<LegalPage />} />
              <Route
                path="/group-buy"
                element={
                  <FeatureRoute flag="group_buy">
                    <GroupBuy />
                  </FeatureRoute>
                }
              />
              <Route
                path="/language-exchange"
                element={
                  <FeatureRoute flag="language_exchange">
                    <LanguageExchange />
                  </FeatureRoute>
                }
              />
              <Route
                path="/events"
                element={
                  <FeatureRoute flag="events">
                    <Events />
                  </FeatureRoute>
                }
              />
              <Route
                path="/group-buy/write"
                element={
                  <FeatureRoute flag="group_buy">
                    <ProtectedRoute>
                      <GroupBuyWrite />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/visa-run"
                element={
                  <FeatureRoute flag="visa_run">
                    <VisaRun />
                  </FeatureRoute>
                }
              />
              <Route
                path="/visa-run/write"
                element={
                  <FeatureRoute flag="visa_run">
                    <ProtectedRoute>
                      <VisaRunWrite />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/business-transfer"
                element={
                  <FeatureRoute flag="business_transfer">
                    <BusinessTransfer />
                  </FeatureRoute>
                }
              />
              <Route
                path="/business-transfer/write"
                element={
                  <FeatureRoute flag="business_transfer">
                    <ProtectedRoute>
                      <BusinessTransferWrite />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/fortune"
                element={
                  <FeatureRoute flag="fortune">
                    <FortuneMenu />
                  </FeatureRoute>
                }
              />
              <Route
                path="/fortune/today"
                element={
                  <FeatureRoute flag="fortune">
                    <OdaysFortune />
                  </FeatureRoute>
                }
              />
              <Route
                path="/fortune/horoscope"
                element={
                  <FeatureRoute flag="fortune">
                    <Horoscope />
                  </FeatureRoute>
                }
              />
              <Route
                path="/fortune/zodiac"
                element={
                  <FeatureRoute flag="fortune">
                    <ZodiacFortune />
                  </FeatureRoute>
                }
              />
              <Route
                path="/fortune/year"
                element={
                  <FeatureRoute flag="fortune">
                    <YearFortune />
                  </FeatureRoute>
                }
              />
              <Route
                path="/fortune/astrology"
                element={
                  <FeatureRoute flag="fortune">
                    <Astrology />
                  </FeatureRoute>
                }
              />
              <Route
                path="/fortune/tarot"
                element={
                  <FeatureRoute flag="fortune">
                    <Tarot />
                  </FeatureRoute>
                }
              />
              <Route
                path="/fortune/saju"
                element={
                  <FeatureRoute flag="fortune">
                    <Saju />
                  </FeatureRoute>
                }
              />
              <Route
                path="/fortune/tojeong"
                element={
                  <FeatureRoute flag="fortune">
                    <Tojeong />
                  </FeatureRoute>
                }
              />
              <Route
                path="/fortune/compatibility"
                element={
                  <FeatureRoute flag="fortune">
                    <Compatibility />
                  </FeatureRoute>
                }
              />
              <Route
                path="/fortune/dream"
                element={
                  <FeatureRoute flag="fortune">
                    <DreamFortune />
                  </FeatureRoute>
                }
              />
              <Route
                path="/fortune/name"
                element={
                  <FeatureRoute flag="fortune">
                    <NameFortune />
                  </FeatureRoute>
                }
              />
              <Route path="/listings" element={<ListingHub />} />
              <Route path="/listings/c/:categoryId" element={<CategoryListingsPage />} />
              <Route path="/listings/i/:listingId" element={<ListingDetailPage />} />
              <Route path="/transfer" element={<BookingTransferList />} />
              <Route path="/transfer/write" element={<BookingTransferWrite />} />
              <Route path="/transfer/:listingId" element={<BookingTransferDetail />} />
              <Route path="/succession" element={<SuccessionList />} />
              <Route path="/succession/write" element={<SuccessionWrite />} />
              <Route path="/succession/:listingId" element={<SuccessionDetail />} />
              <Route path="/local/hospital" element={<LocalHospital />} />
              <Route path="/local/restaurant/explore" element={<LocalRestaurantExplore />} />
              <Route path="/local/restaurant" element={<Navigate to="/restaurant" replace />} />
              <Route
                path="/local/visa"
                element={
                  <FeatureRoute flag="local_visa_agency">
                    <LocalVisa />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/beauty"
                element={
                  <FeatureRoute flag="local_beauty">
                    <LocalBeauty />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/cleaning"
                element={
                  <FeatureRoute flag="local_cleaning">
                    <LocalCleaning />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/return"
                element={
                  <FeatureRoute flag="local_return">
                    <LocalReturn />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/supermarket"
                element={
                  <FeatureRoute flag="local_supermarket">
                    <LocalSupermarket />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/dental"
                element={
                  <FeatureRoute flag="local_dental">
                    <LocalDental />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/laundry"
                element={
                  <FeatureRoute flag="local_laundry">
                    <LocalLaundry />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/repair"
                element={
                  <FeatureRoute flag="local_repair">
                    <LocalRepair />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/babysitter"
                element={
                  <FeatureRoute flag="local_babysitter">
                    <LocalBabysitter />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/translation"
                element={
                  <FeatureRoute flag="local_translation">
                    <LocalTranslation />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/rental"
                element={
                  <FeatureRoute flag="rental">
                    <Rental />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/visa-service"
                element={
                  <FeatureRoute flag="visa_service">
                    <VisaService />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/professional"
                element={
                  <FeatureRoute flag="professional">
                    <ProfessionalServices />
                  </FeatureRoute>
                }
              />
              <Route path="/local/academy" element={<Navigate to="/academy" replace />} />
              <Route
                path="/local/activity"
                element={
                  <FeatureRoute flag="local_activity">
                    <ActivityList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/pet"
                element={
                  <FeatureRoute flag="local_pet">
                    <PetServices />
                  </FeatureRoute>
                }
              />
              <Route
                path="/pet/register"
                element={
                  <FeatureRoute flag="local_pet">
                    <ProtectedRoute>
                      <PetRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/mentoring"
                element={
                  <FeatureRoute flag="local_mentoring">
                    <MentorList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/mentor/register"
                element={
                  <FeatureRoute flag="local_mentoring">
                    <ProtectedRoute>
                      <MentorRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/korean-shop"
                element={
                  <FeatureRoute flag="local_korean_shop">
                    <KoreanProducts />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/wedding"
                element={
                  <FeatureRoute flag="local_wedding">
                    <WeddingServices />
                  </FeatureRoute>
                }
              />
              <Route
                path="/wedding/register"
                element={
                  <FeatureRoute flag="local_wedding">
                    <ProtectedRoute>
                      <WeddingRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/airport-pickup"
                element={
                  <FeatureRoute flag="local_airport_pickup">
                    <AirportPickup />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/insurance"
                element={
                  <FeatureRoute flag="local_insurance">
                    <InsuranceList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/insurance/register"
                element={
                  <FeatureRoute flag="local_insurance">
                    <ProtectedRoute>
                      <InsuranceRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/medicine"
                element={<Medicine />}
              />
              <Route
                path="/medicine/:id"
                element={<MedicineDetail />}
              />
              <Route
                path="/local/remittance"
                element={
                  <FeatureRoute flag="local_remittance">
                    <RemittanceList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/remittance/register"
                element={
                  <FeatureRoute flag="local_remittance">
                    <ProtectedRoute>
                      <RemittanceRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/settlement"
                element={
                  <FeatureRoute flag="local_settlement">
                    <SettlementPackage />
                  </FeatureRoute>
                }
              />
              <Route
                path="/education"
                element={<Education />}
              />
              <Route
                path="/local/schools"
                element={
                  <FeatureRoute flag="local_schools">
                    <SchoolList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/schools/compare"
                element={
                  <FeatureRoute flag="local_schools">
                    <SchoolCompare />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/education-consultant"
                element={
                  <FeatureRoute flag="local_education_consult">
                    <EducationConsultantList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/fitness"
                element={
                  <FeatureRoute flag="local_fitness">
                    <FitnessList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/golf"
                element={
                  <FeatureRoute flag="golf">
                    <GolfList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/medical-tourism"
                element={
                  <FeatureRoute flag="medical_tourism">
                    <MedicalTourism />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/boat"
                element={
                  <FeatureRoute flag="local_boat">
                    <BoatRental />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/photo"
                element={
                  <FeatureRoute flag="local_photo">
                    <PhotoService />
                  </FeatureRoute>
                }
              />
              <Route
                path="/photo/register"
                element={
                  <FeatureRoute flag="local_photo">
                    <ProtectedRoute>
                      <PhotoRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/event-planner"
                element={
                  <FeatureRoute flag="local_event_planner">
                    <EventPlanner />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/digital"
                element={
                  <FeatureRoute flag="local_digital">
                    <DigitalServices />
                  </FeatureRoute>
                }
              />
              <Route
                path="/digital/register"
                element={
                  <FeatureRoute flag="local_digital">
                    <ProtectedRoute>
                      <DigitalRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/tours"
                element={
                  <FeatureRoute flag="local_tours">
                    <TourList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/interior"
                element={
                  <FeatureRoute flag="local_interior">
                    <InteriorList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/interior/register"
                element={
                  <FeatureRoute flag="local_interior">
                    <ProtectedRoute>
                      <InteriorRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/local/subscription"
                element={
                  <FeatureRoute flag="local_subscription">
                    <SubscriptionBox />
                  </FeatureRoute>
                }
              />
              <Route
                path="/talents"
                element={
                  <FeatureRoute flag="talent_market">
                    <TalentMarket />
                  </FeatureRoute>
                }
              />
              <Route
                path="/talents/:id"
                element={
                  <FeatureRoute flag="talent_market">
                    <TalentDetail />
                  </FeatureRoute>
                }
              />
              <Route
                path="/talents/register"
                element={
                  <FeatureRoute flag="talent_market">
                    <ProtectedRoute>
                      <TalentRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/groups"
                element={
                  <FeatureRoute flag="groups">
                    <Navigate to="/community?tab=group" replace />
                  </FeatureRoute>
                }
              />
              <Route
                path="/my/groups"
                element={
                  <FeatureRoute flag="groups">
                    <MyGroups />
                  </FeatureRoute>
                }
              />
              <Route
                path="/groups/create"
                element={
                  <FeatureRoute flag="groups">
                    <ProtectedRoute>
                      <GroupCreate />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/groups/:groupId/manage"
                element={
                  <FeatureRoute flag="groups">
                    <ProtectedRoute>
                      <GroupManage />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/groups/:groupId"
                element={
                  <FeatureRoute flag="groups">
                    <GroupDetail />
                  </FeatureRoute>
                }
              />
              <Route
                path="/group-chats"
                element={
                  <FeatureRoute flag="group_chat">
                    <GroupChatList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/luxury"
                element={
                  <FeatureRoute flag="luxury_used">
                    <LuxuryMarket />
                  </FeatureRoute>
                }
              />
              <Route
                path="/booking"
                element={<BookingHub />}
              />
              <Route
                path="/flights"
                element={
                  <FeatureRoute flag="flights">
                    <FlightSearch />
                  </FeatureRoute>
                }
              />
              <Route
                path="/hotel"
                element={
                  <FeatureRoute flag="hotel">
                    <Hotel />
                  </FeatureRoute>
                }
              />
              <Route path="/pool-villa" element={<PoolVilla />} />
              <Route path="/pool-villa/:id" element={<PoolVillaDetail />} />
              <Route
                path="/pool-villa/register"
                element={
                  <ProtectedRoute>
                    <PoolVillaRegister />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hotel/register"
                element={
                  <FeatureRoute flag="hotel">
                    <ProtectedRoute>
                      <HotelRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/hotel/:id"
                element={
                  <FeatureRoute flag="hotel">
                    <HotelDetail />
                  </FeatureRoute>
                }
              />
              <Route
                path="/hospital"
                element={
                  <FeatureRoute flag="hospital">
                    <Hospital />
                  </FeatureRoute>
                }
              />
              <Route
                path="/hospital/register"
                element={
                  <FeatureRoute flag="hospital">
                    <ProtectedRoute>
                      <HospitalRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/hospital/:id"
                element={
                  <FeatureRoute flag="hospital">
                    <HospitalDetail />
                  </FeatureRoute>
                }
              />
              <Route
                path="/visa-service"
                element={
                  <FeatureRoute flag="visa_service">
                    <VisaService />
                  </FeatureRoute>
                }
              />
              <Route
                path="/visa-service/register"
                element={
                  <FeatureRoute flag="visa_service">
                    <ProtectedRoute>
                      <VisaServiceRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/visa-service/:id"
                element={
                  <FeatureRoute flag="visa_service">
                    <VisaServiceDetail />
                  </FeatureRoute>
                }
              />
              <Route
                path="/rental"
                element={
                  <FeatureRoute flag="rental">
                    <Rental />
                  </FeatureRoute>
                }
              />
              <Route
                path="/rental/my-bookings"
                element={
                  <FeatureRoute flag="rental">
                    <ProtectedRoute>
                      <RentalMyBookings />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/my-bookings"
                element={
                  <FeatureRoute flag="rental">
                    <ProtectedRoute>
                      <RentalMyBookings />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/rental/register"
                element={
                  <FeatureRoute flag="rental">
                    <ProtectedRoute>
                      <RentalRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/rental/:id"
                element={
                  <FeatureRoute flag="rental">
                    <RentalDetail />
                  </FeatureRoute>
                }
              />
              <Route
                path="/live"
                element={
                  <FeatureRoute flag="live_commerce">
                    <LiveStream />
                  </FeatureRoute>
                }
              />
              <Route
                path="/crowdfunding"
                element={
                  <FeatureRoute flag="crowdfunding">
                    <Crowdfunding />
                  </FeatureRoute>
                }
              />
              <Route
                path="/real-estate"
                element={
                  <FeatureRoute flag="real_estate">
                    <RealEstate />
                  </FeatureRoute>
                }
              />
              <Route
                path="/my-properties"
                element={
                  <FeatureRoute flag="real_estate">
                    <ProtectedRoute>
                      <MyProperties />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route path="/all-services" element={<AllServicesPage />} />
              <Route path="/hub/food-mart" element={<Navigate to="/hub/food_cafe_mart" replace />} />
              <Route path="/hub/food_cafe_mart" element={<FoodMartHubPage />} />
              <Route path="/hub/food_cafe_mart/events" element={<FoodMartEventsHubPage />} />
              <Route path="/hub/food_cafe_mart/cuisine/:cuisineKey" element={<CuisineCategoryPage />} />
              <Route path="/location/area-settings" element={<LocationAreaSettingsPage />} />
              <Route path="/hub/living_realestate_rental" element={<Navigate to="/real-estate" replace />} />
              <Route path="/travel" element={<TravelHub />} />
              <Route path="/hub/:category" element={<GenericHubPage />} />
              <Route path="/webtoon" element={<WebtoonPage />} />
              <Route path="/mingo-story" element={<Navigate to="/shorts" replace />} />
              <Route path="/shorts" element={<ShortsPage />} />
              <Route path="/shorts/create" element={<ProtectedRoute><StoryCreatePage /></ProtectedRoute>} />
              <Route
                path="/story/revenue"
                element={
                  <ProtectedRoute>
                    <StoryRevenuePanel />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/story/translations"
                element={
                  <ProtectedRoute>
                    <StoryTranslationDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/local/coworking"
                element={
                  <FeatureRoute flag="coworking">
                    <CoworkingList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/coworking/register"
                element={
                  <FeatureRoute flag="coworking">
                    <ProtectedRoute>
                      <CoworkingRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/recipes"
                element={
                  <FeatureRoute flag="recipes">
                    <RecipeList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/auctions"
                element={
                  <FeatureRoute flag="property_auction">
                    <PropertyAuction />
                  </FeatureRoute>
                }
              />
              <Route
                path="/map"
                element={
                  <FeatureRoute flag="map_search">
                    <MapSearch />
                  </FeatureRoute>
                }
              />
              <Route
                path="/newsletters"
                element={
                  <FeatureRoute flag="newsletter">
                    <NewsletterList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/auction"
                element={
                  <FeatureRoute flag="auction">
                    <ProductAuction />
                  </FeatureRoute>
                }
              />
              <Route
                path="/rider"
                element={
                  <FeatureRoute flag="local_rider">
                    <RiderSignup />
                  </FeatureRoute>
                }
              />
              <Route
                path="/financial"
                element={
                  <ProtectedRoute>
                    <FinancialPlanner />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/point-market"
                element={
                  <FeatureRoute flag="point_market">
                    <ProtectedRoute>
                      <PointMarket />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path="/guides"
                element={
                  <FeatureRoute flag="guide_market">
                    <GuideMarket />
                  </FeatureRoute>
                }
              />
              <Route
                path="/guides/register"
                element={
                  <FeatureRoute flag="guide_market">
                    <ProtectedRoute>
                      <GuideRegister />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
              <Route path="/guide-market" element={<Navigate to="/guides" replace />} />
              <Route path="/guide-market/:id" element={<GuideMarketDetailLegacyRedirect />} />
              <Route
                path="/guides/:id"
                element={
                  <FeatureRoute flag="guide_market">
                    <GuideDetail />
                  </FeatureRoute>
                }
              />
              <Route
                path="/sos"
                element={
                  <FeatureRoute flag="sos">
                    <SosPage />
                  </FeatureRoute>
                }
              />
              <Route
                path="/lost-found"
                element={
                  <ProtectedRoute>
                    <LostAndFound />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/group-delivery"
                element={
                  <FeatureRoute flag="group_delivery">
                    <GroupDelivery />
                  </FeatureRoute>
                }
              />
              <Route
                path="/interpreter"
                element={
                  <FeatureRoute flag="interpreter">
                    <InterpreterService />
                  </FeatureRoute>
                }
              />
              <Route
                path="/rewards"
                element={
                  <ProtectedRoute>
                    <Rewards />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/carpool"
                element={
                  <FeatureRoute flag="local_carpool">
                    <CarpoolBoard />
                  </FeatureRoute>
                }
              />
              <Route
                path="/deals"
                element={
                  <FeatureRoute flag="deals">
                    <LocalDeals />
                  </FeatureRoute>
                }
              />
              <Route
                path="/vehicles"
                element={
                  <FeatureRoute flag="vehicles">
                    <VehicleList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/news"
                element={
                  <FeatureRoute flag="news">
                    <NewsPage />
                  </FeatureRoute>
                }
              />
              <Route
                path="/surveys"
                element={
                  <FeatureRoute flag="surveys">
                    <SurveyList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/advertiser"
                element={
                  <ProtectedRoute>
                    <AdvertiserDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/currency-exchange"
                element={
                  <FeatureRoute flag="currency_exchange">
                    <CurrencyExchange />
                  </FeatureRoute>
                }
              />
              <Route
                path="/currency-exchange/alert"
                element={
                  <FeatureRoute flag="currency_exchange">
                    <ExchangeAlert />
                  </FeatureRoute>
                }
              />
              <Route
                path="/currency-exchange/p/:id"
                element={
                  <FeatureRoute flag="currency_exchange">
                    <ExchangeDetail />
                  </FeatureRoute>
                }
              />
              <Route
                path="/currency-exchange/new"
                element={
                  <FeatureRoute flag="currency_exchange">
                    <ProtectedRoute>
                      <ExchangeWrite />
                    </ProtectedRoute>
                  </FeatureRoute>
                }
              />
            </Route>
            <Route path="*" element={<CatchAllRedirect />} />
          </Routes>
        </Suspense>
        </RouteErrorBoundary>
      </BrowserRouter>
      </div>
      </ChatInboxProvider>
    </>
  );
}

export default function App() {
  return (
    <MingoProvider>
      <SessionGate>
        <AppShell />
      </SessionGate>
    </MingoProvider>
  );
}
